/**
 * Artvault store
 */
import { getPersistedValue } from '@/helpers/store.js';

const maxAge = 345600000; // 96 hours (4 days) [msec]

const initialState = () => ({
    workspaces: [],
    workspacesArtworks: {}, // Workspace Id is the key
    fileLibraries: [],
    fileLibrariesFolders: {}, // FileLibrary Id is the key

    myFileLibrary: {}, // DEPRECATED
    myFileLibraryItems: [], // DEPRECATED
    myWorkspace: {}, // DEPRECATED
    myWorkspaceItems: [], // DEPRECATED

    timestamps: {}
});

const createStore = (Vue) => {

    const state = initialState;

    const getters = {

        getWorkspaces (state) {
            return state.workspaces;
        },

        getWorkspaceArtworks (state, id) {
            return state.workspacesArtworks[id] || [];
        },

        getFileLibraries (state) {
            return state.fileLibraries;
        },

        getFileLibraryFolders (state, id) {
            return state.fileLibrariesFolders[id] || [];
        },

        getMyFileLibrary (state) { // DEPRECATED
            return state.myFileLibrary;
        },

        /**
         * This is called from the context of pagination-tiles
         */
        getMyFileLibraryItems (state) { // DEPRECATED
            const response = {
                data: {
                    Results: state.myFileLibraryItems || []
                }
            };
            return response;
        },

        getMyWorkspace (state) { // DEPRECATED
            return state.myWorkspace;
        },

        getMyWorkspaceItems (state) { // DEPRECATED
            return state.myWorkspaceItems;
        }
    };

    const actions = {
        async fetchWorkspaces ({ commit, state }) {
            // check persisted localStorage
            const value = getPersistedValue(state, 'workspaces', maxAge);
            if (value) {
                return Promise.resolve(value);
            }

            // fetch
            return new Promise((resolve, reject) => {
                Vue.prototype.$SVC.getWorkspaces()
                    .then((response) => {
                        const items = response && response.data && response.data.Results;
                        commit('setWorkspaces', items);
                        resolve(items);
                    })
                    .catch(reject);
            });
        },

        async fetchWorkspaceArtworks ({ commit, state }, workspaceId) {
            if (!workspaceId) {
                return Promise.reject(new Error('No Workspace Id'));
            }

            // check persisted localStorage
            const workspacesArtworks = getPersistedValue(state, 'workspacesArtworks', maxAge);
            const value = workspacesArtworks && workspacesArtworks[workspaceId];
            if (value) {
                return Promise.resolve(value);
            }

            // fetch
            return new Promise((resolve, reject) => {
                Vue.prototype.$SVC.getWorkspaceArtworks(workspaceId)
                    .then((response) => {
                        const items = response && response.data && response.data.Results;
                        commit('updateWorkspacesArtworks', {[workspaceId]: items}); // ES6 dynamic key
                        resolve(items);
                    })
                    .catch(reject);
            });
        },

        async fetchFileLibraries ({ commit, state }) {
            // check persisted localStorage
            const value = getPersistedValue(state, 'fileLibraries', maxAge);
            if (value) {
                return Promise.resolve(value);
            }

            // fetch
            return new Promise((resolve, reject) => {
                Vue.prototype.$SVC.getFileLibraries()
                    .then((response) => {
                        const items = response && response.data && response.data.Results;
                        commit('setFileLibraries', items);
                        resolve(items);
                    })
                    .catch(reject);
            });
        },

        /**
         * Fetch FileLibraryFolders.
         * Param `folders` is an object, {Name: '<folder name>'}
         */
        async fetchFileLibraryFolders ({ commit, state }, fileLibrary) {
            if (!fileLibrary) {
                return Promise.reject(new Error('No FileLibrary'));
            }

            const fileLibraryId = fileLibrary.Id;
            const folders = fileLibrary.SubFolders;

            // check persisted localStorage
            const fileLibrariesFolders = getPersistedValue(state, 'fileLibrariesFolders', maxAge) || {};
            const value = fileLibrariesFolders && fileLibrariesFolders[fileLibraryId];
            if (value) {
                return Promise.resolve(value);
            }

            // fetch Folders (with ES6 "for" construct)
            const errors = [];
            const fileLibraryFolders = []; // Folders for this FileLib
            for (const subFolder of folders) {
                await Vue.prototype.$SVC.getFileLibraryFolder(fileLibraryId, subFolder.Name)
                    .then((response) => {
                        const result = (response && response.data && response.data.Result) || [];

                        // update Name into result
                        if (result && !result.Name) {
                            result.Name = subFolder.Name;
                        }

                        // update array
                        let index = folders.indexOf(subFolder);
                        if (index < 0) {
                            // Folders for this FileLibrary not found in store, append
                            index = folders.length;
                        }
                        fileLibraryFolders[index] = result;
                    })
                    .catch((error) => {
                        errors.push(error);
                    });
            }
            // the above ES6 construct allows to "await" for the async folders to complete

            if (!errors.length) {
                commit('updateFileLibrariesFolders', {[fileLibraryId]: fileLibraryFolders}); // ES6 dynamic key (use Id, not string "fileLibraryId")
                return Promise.resolve(fileLibraryFolders);
            }
            else {
                return Promise.reject(errors[0]);
            }
        },

        async fetchMyFileLibraryId () { // DEPRECATED
            return new Promise((resolve, reject) => {
                Vue.prototype.$store.dispatch('user/fetchUserProfile', null, {root: true})
                    .then((profile) => {
                        try {
                            const id = profile._links.filelibrary.href.replace('/svc/filelibraries/', '');
                            resolve(id);
                        }
                        catch (error) {
                            resolve(null);
                        }
                    })
                    .catch(reject);
            });
        },

        async fetchMyFileLibrary ({ commit, state }) { // DEPRECATED
            // check persisted localStorage
            const value = getPersistedValue(state, 'myFileLibrary', maxAge);
            if (value) {
                return Promise.resolve(value);
            }

            // fetch
            return new Promise((resolve, reject) => {
                Vue.prototype.$store.dispatch('artvault/fetchMyFileLibraryId')
                    .then((id) => {
                        if (!id) {
                            return resolve(null); // no MyFileLibrary
                        }
                        Vue.prototype.$SVC.getFileLibrary(id)
                            .then((response) => {
                                const myFileLibrary = response && response.data && response.data.Result;
                                // NOTE: FileLibrary response could contain Id
                                if (!myFileLibrary.Id) {
                                    myFileLibrary.Id = id;
                                }
                                commit('setMyFileLibrary', myFileLibrary);
                                resolve(myFileLibrary);
                            })
                            .catch(reject);
                    })
                    .catch(reject);
            });
        },

        async fetchMyFileLibraryItems ({ commit, state }) { // DEPRECATED
            // check persisted localStorage
            const value = getPersistedValue(state, 'myFileLibraryItems', maxAge);
            if (value) {
                return Promise.resolve(value);
            }

            // fetch
            return new Promise((resolve, reject) => {
                Vue.prototype.$store.dispatch('artvault/fetchMyFileLibrary')
                    .then((fileLibrary) => {
                        if (!fileLibrary) {
                            return resolve(null);
                        }

                        // imitate SVC API
                        const items = _.map(fileLibrary.Files, (file, index) => {
                            if (file.MediaType === 'Image' && file.ImageInfo) {
                                return {
                                    Id: 'Files' + index,
                                    ImageUrl: file.ImageInfo.Url
                                };
                            }
                        });
                        commit('setMyFileLibraryItems', items);
                        resolve(items);

                        // const folderId = 1;
                        // const subFolders = 1;
                        // Vue.prototype.$SVC.getFileLibraryFolder(fileLibraryId, folderId, subFolders)
                        //     .then((response) => {
                        //         const folder = response && response.data && response.data.Results;
                        //         resolve(folder.Files);
                        //     })
                        //     .catch(reject);
                    })
                    .catch(reject);
            });
        },

        async fetchMyWorkspaceId () { // DEPRECATED
            return new Promise((resolve, reject) => {
                Vue.prototype.$store.dispatch('user/fetchUserProfile', null, {root: true})
                    .then((profile) => {
                        try {
                            const id = profile._links.workspace.href.replace('/svc/workspaces/', '');
                            resolve(id);
                        }
                        catch (error) {
                            resolve(null);
                        }
                    })
                    .catch(reject);
            });
        },

        async fetchMyWorkspace ({ commit, state }) { // DEPRECATED
            // check persisted localStorage
            const value = getPersistedValue(state, 'myWorkspace', maxAge);
            if (value) {
                return Promise.resolve(value);
            }

            // fetch
            return new Promise((resolve, reject) => {
                Vue.prototype.$store.dispatch('artvault/fetchMyWorkspaceId')
                    .then((id) => {
                        if (!id) {
                            return resolve(null); // no MyWorkspace
                        }
                        Vue.prototype.$SVC.getWorkspace(id)
                            .then((response) => {
                                const workspace = response && response.data && response.data.Result;
                                commit('setMyWorkspace', workspace);
                                resolve(workspace);
                            })
                            .catch(reject);
                    })
                    .catch(reject);
            });
        },

        async fetchMyWorkspaceItems ({ commit, state }) { // DEPRECATED
            // check persisted localStorage
            const value = getPersistedValue(state, 'myWorkspaceItems', maxAge);
            if (value) {
                return Promise.resolve(value);
            }

            // fetch
            return new Promise((resolve, reject) => {
                Vue.prototype.$store.dispatch('artvault/fetchMyWorkspace')
                    .then((workspace) => {
                        if (!workspace) {
                            return resolve(null);
                        }
                        // imitate SVC API
                        // const items = _.map(fileLibrary.Files, (file, index) => {
                        //     if (file.MediaType === 'Image' && file.ImageInfo) {
                        //         return {
                        //             Id: 'Files' + index,
                        //             ImageUrl: file.ImageInfo.Url
                        //         };
                        //     }
                        // });

                        const items = [];
                        commit('setMyWorkspaceItems', items);
                        resolve(items);

                        // const folderId = 1;
                        // const subFolders = 1;
                        // Vue.prototype.$SVC.getFileLibraryFolder(fileLibraryId, folderId, subFolders)
                        //     .then((response) => {
                        //         const folder = response && response.data && response.data.Results;
                        //         resolve(folder.Files);
                        //     })
                        //     .catch(reject);
                    })
                    .catch(reject);
            });
        }
    };

    const mutations = {
        RESET (state) {
            const newState = initialState();
            Object.keys(newState).forEach((key) => {
                state[key] = newState[key];
            });
        },

        setFileLibraries (state, value) {
            state.fileLibraries = value || [];
            state.timestamps.fileLibraries = new Date();
        },

        setWorkspaces (state, value) {
            state.workspaces = value || [];
            state.timestamps.workspaces = new Date();
        },

        updateWorkspacesArtworks (state, value) {
            state.workspacesArtworks = _.extend({}, state.workspacesArtworks, value);
            // store only the oldest date
            if (!state.timestamps.workspacesArtworks) {
                state.timestamps.workspacesArtworks = new Date();
            }
        },

        updateFileLibrariesFolders (state, value) {
            state.fileLibrariesFolders = _.extend({}, state.fileLibrariesFolders, value);
            // store only the oldest date
            if (!state.timestamps.fileLibrariesFolders) {
                state.timestamps.fileLibrariesFolders = new Date();
            }
        },

        setMyFileLibrary (state, value) {
            state.myFileLibrary = value || {};
            state.timestamps.myFileLibrary = new Date();
        },

        setMyFileLibraryItems (state, value) {
            state.myFileLibraryItems = value || [];
            state.timestamps.myFileLibraryItems = new Date();
        },

        setMyWorkspace (state, value) {
            state.myWorkspace = value || {};
            state.timestamps.myWorkspace = new Date();
        },

        setMyWorkspaceItems (state, value) {
            state.myWorkspaceItems = value || [];
            state.timestamps.myWorkspaceItems = new Date();
        }
    };

    return {
        namespaced: true,
        state,
        getters,
        actions,
        mutations
    };
};

export default app => createStore(app);
